import { Input } from '@src/components/ui/input';

import { FileCardFlow } from './FileCardFlow';
import { FileContent } from './FilePreview';
import { ProcessFieldFileUploadAiChat } from './ProcessFieldDynamic/ProcessFieldFile/ProcessFieldFileUploadAiChat';

import { css, cx } from '@emotion/css';
import { ShadcnDrawerProps } from '@shadcn-features/core';
import { Paperclip } from 'lucide-react';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export interface ShadcnUploadFileVM {
    id: string;
    name: string;
    extension?: string;
}

type ApiFileResponse = {
    id: string;
    originalFileName: string;
    contentType: string;
    content: string;
    size: number;
};

type LegacyFileResponse = {
    content: string;
    extension: string;
    name: string;
};

export type FileResponse = ApiFileResponse | LegacyFileResponse | FileContent;

export type ShadcnUploadBaseProps = {
    disabled?: boolean;
    children?: ReactNode;
    variant?: 'default' | 'ai-chat';
    uploadText?: string;
    max?: number;
    extensions?: string[];
    accept?: string;
    dragger?: boolean;
    maxSize?: number;
    showFileCard?: boolean;
    fileCardLabel?: string;
    previewWidth?: number;
    previewHeight?: number;
};

export type ShadcnUploadMainProps<T extends ShadcnUploadFileVM> = {
    value?: T[];
    extra?: (file: T) => ReactNode;
    onUpload: (file: File) => Promise<void> | void;
    onDownload?: (
        params: T,
    ) => Promise<FileContent | undefined> | FileContent | undefined;
    onDownloadBlob?: (params: T) => Promise<void> | void;
    downloadLabel?: ReactNode;
    downloadBlobLabel?: ReactNode;
    hideDownloadButton?: boolean;
    onDelete?: (params: T) => unknown;
    onPreview?: (params: T) => Promise<void> | void;
    previewDrawerProps?: ShadcnDrawerProps;
    enablePdfAiAnalysis?: boolean;

    formatsText?: string;
    maxSizeText?: string;
    uploadText?: string;
    disabled?: boolean;
    readOnly?: boolean;
};

export const ShadcnUploadFlowClient = <T extends ShadcnUploadFileVM>({
    value = [],
    onUpload,
    onDelete: onDeleteProps,
    onDownload,
    onDownloadBlob,
    downloadLabel,
    downloadBlobLabel,
    hideDownloadButton,
    onPreview,
    disabled: customDisabled,
    readOnly,
    extra,
    max,
    extensions,
    accept,
    maxSize,
    previewDrawerProps,
    enablePdfAiAnalysis = true,
    showFileCard = false,
    variant = 'default',
}: ShadcnUploadBaseProps & ShadcnUploadMainProps<T>) => {
    const { t } = useTranslation('translation');
    const disabled = (customDisabled || readOnly) ?? false;
    const [loading, setLoading] = useState(false);
    const [isEditingFile, setIsEditingFile] = useState(false);
    const previousValueLengthRef = useRef(value.length);

    const [, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const onDelete = useCallback(
        async (params: T) => {
            setLoading(true);
            try {
                await onDeleteProps?.(params);
            } finally {
                setLoading(false);
            }
        },
        [onDeleteProps],
    );

    const handleFileSelect = useCallback(
        async (file: File) => {
            if (maxSize && file.size / 1024 / 1024 >= maxSize) {
                toast.error(
                    variant === 'ai-chat'
                        ? t('translation:maxFileSize')
                        : 'Max size is ' + maxSize + 'MB',
                );
                return;
            }
            if (extensions && !extensions.includes(file.type)) {
                toast.error(
                    variant === 'ai-chat'
                        ? t('translation:invalidFileType')
                        : 'Invalid file type',
                );
                return;
            }

            setLoading(true);
            try {
                await onUpload(file);
                if (max === 1 && showFileCard) {
                    setIsEditingFile(false);
                }
            } finally {
                setLoading(false);
            }
        },
        [onUpload, maxSize, extensions, max, showFileCard, variant, t],
    );

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (!files) return;

            Array.from(files).forEach((file) => {
                void handleFileSelect(file);
            });

            e.target.value = '';
        },
        [handleFileSelect],
    );

    useEffect(() => {
        if (value.length > 0 && max === 1 && showFileCard) {
            const wasEmpty = previousValueLengthRef.current === 0;
            const isNowFilled = value.length > 0;
            if (wasEmpty && isNowFilled) {
                setIsEditingFile(false);
            }
            previousValueLengthRef.current = value.length;
        } else if (value.length === 0) {
            previousValueLengthRef.current = 0;
        }
    }, [value.length, max, showFileCard]);

    const canUpload =
        typeof max !== 'number' || max > value.length || isEditingFile;
    const hasFile = value.length > 0 && max === 1 && showFileCard;
    const showUploadButton = canUpload && (!hasFile || isEditingFile);

    const handleDragOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (!disabled) {
                setIsDragging(true);
            }
        },
        [disabled],
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            if (disabled || loading) return;

            const files = e.dataTransfer.files;
            if (files && files.length > 0) {
                void handleFileSelect(files[0]);
            }
        },
        [disabled, loading, handleFileSelect],
    );

    const handleClick = useCallback(() => {
        if (!disabled && !loading) {
            fileInputRef.current?.click();
        }
    }, [disabled, loading]);

    if (variant === 'ai-chat') {
        return (
            <ProcessFieldFileUploadAiChat
                files={value}
                disabled={disabled || loading}
                readOnly={readOnly}
                canUpload={canUpload}
                accept={accept}
                inputRef={fileInputRef}
                onInputChange={handleInputChange}
                onClick={handleClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onDownload={onDownload}
                onDelete={onDeleteProps ? onDelete : undefined}
            />
        );
    }

    return (
        <div
            className={css`
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
            `}
        >
            {showUploadButton && (
                <div
                    className={cx(
                        'relative h-(--input-size) rounded-lg border border-(--color-brand-green)!',
                        'flex cursor-pointer items-center',
                        !(disabled || readOnly) &&
                            'hover:bg-(--color-bg-primary) hover:text-(--color-brand-green)',
                        (disabled || readOnly) && 'cursor-default! opacity-50',
                    )}
                    onClick={handleClick}
                >
                    <div className='pointer-events-auto absolute inset-0 flex w-fit items-center gap-4 px-4'>
                        <Paperclip
                            className='text-(--color-brand-green)'
                            size={20}
                        />
                        <span className='text-sm font-medium text-nowrap'>
                            {t('translation:selectFile')}
                        </span>
                    </div>
                    <div
                        className={cx('h-full w-full')}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <Input
                            ref={fileInputRef}
                            accept={accept}
                            disabled={disabled || loading}
                            type='file'
                            className={css`
                                position: absolute;
                                opacity: 0;
                                width: 0;
                                height: 0;
                                pointer-events: none;
                            `}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
            )}
            {!value.length && disabled && !showUploadButton && (
                <div
                    className={cx(
                        `relative flex h-(--input-size) cursor-pointer items-center rounded-lg border px-4`,
                    )}
                >
                    {t('translation:noFile')}
                </div>
            )}
            {!showFileCard &&
                value.map((x) => (
                    <FileCardFlow
                        key={x.id}
                        disabled={disabled}
                        enablePdfAiAnalysis={enablePdfAiAnalysis}
                        extra={extra}
                        file={x}
                        previewDrawerProps={previewDrawerProps}
                        wrapperTypePreview='modal'
                        onDelete={onDelete}
                        onDownload={onDownload}
                        onPreview={onPreview}
                    />
                ))}
        </div>
    );
};

export default ShadcnUploadFlowClient;
